import { useCallback, useEffect, useState } from "react";
import AppLayout from "../layouts/AppLayout.jsx";
import ImageCard from "../components/ImageCard.jsx";
import ReelCard from "../components/ReelCard.jsx";
import API from "../config/api.js";
import { useMyMediaUpload } from "../context/MyMediaUploadContext.jsx";
import NavMyMedia from "../components/NavMyMedia.jsx";

export default function MyMedia() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantityImages, setQuantityImages] = useState(0);
  const [imageLimit, setImageLimit] = useState(0);
  const [playingReelId, setPlayingReelId] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const { openUpload } = useMyMediaUpload();

  const fetchMedia = useCallback(async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API.drafts}?listMyMedia`, {
        method: "POST",
        credentials: "include",
      });

      const data = await res.json();

      const items = Array.isArray(data?.media) ? data.media : [];
      setQuantityImages(Number(data?.quantityImages || 0));
      setImageLimit(Number(data?.imageLimit || 0));
      setMedia(items);
    } catch (err) {
      console.error("Error", err);
      setMedia([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const filteredMedia = media.filter((item) => {
    if (activeFilter === "photos") return item?.type === "image";
    if (activeFilter === "videos") return item?.type === "reel";
    return true;
  });

  const progressPercent =
    imageLimit > 0 ? Math.min((quantityImages * 100) / imageLimit, 100) : 0;

  const handleUpload = () => openUpload({ type: "combined", prompt: "0" });

  const handleTogglePreview = (idreel) =>
    setPlayingReelId((prev) => (prev === idreel ? null : idreel));

  const handleVideoEnded = () => setPlayingReelId(null);

  const handleImageRemoved = (idimage) => {
    setMedia((prev) =>
      prev.filter(
        (item) => !(item?.type === "image" && item?.data?.idimage === idimage)
      )
    );
    setQuantityImages((prev) => Math.max(prev - 1, 0));
  };

  const handleReelRemoved = (idreel) => {
    setMedia((prev) =>
      prev.filter(
        (item) => !(item?.type === "reel" && item?.data?.idreel === idreel)
      )
    );
  };

  const handleImageNoteSaved = (idimage, note) => {
    setMedia((prev) =>
      prev.map((item) =>
        item?.type === "image" && item?.data?.idimage === idimage
          ? { ...item, data: { ...item.data, note } }
          : item
      )
    );
  };

  const placeholders = Array.from({ length: 8 }, (_, i) => (
    <div key={`placeholder-${i}`} className="col-6 col-md-3">
      <div className="card rounded-4 h-100 border animate__animated animate__fadeIn animate__slow">
        <div className="placeholder-glow">
          <div
            className="placeholder w-100 rounded-top-4"
            style={{ height: "200px" }}
          />
        </div>
        <div className="card-body">
          <h5 className="card-title">
            <span className="placeholder col-7" />
          </h5>
          <p className="card-text">
            <span className="placeholder col-5" />
          </p>
        </div>
        <div className="card-footer rounded-bottom-4 bg-transparent p-3">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                type="button"
                className="btn btn-outline-primary disabled placeholder"
                disabled
              >
                Loading...
              </button>
            </div>
            <div className="col text-end">
              <span className="placeholder col-1 me-2" />
              <span className="placeholder col-1" />
            </div>
          </div>
        </div>
      </div>
    </div>
  ));

  return (
    <AppLayout currentPage="my-media">
      <NavMyMedia currentPage="my-media" />

      <div className="row g-2 align-items-center my-4">
        <div className="col-12 col-md-3">
          <h6 className="mb-0 fs-6">
            Files
            <strong className="float-end small text-muted">
              {quantityImages}/{imageLimit}
            </strong>
          </h6>
          <div className="progress mt-1" style={{ height: "2px" }}>
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${progressPercent}%` }}
              aria-valuenow={progressPercent}
              aria-valuemin="0"
              aria-valuemax="100"
            />
          </div>
        </div>

        <div className="col-6 col-md-5 text-start hide-if-app-store">
          <small className="d-inline-block ms-2 d-none d-xl-inline-block" />
        </div>

        <div className="col-12 col-md-4 text-center text-md-end">
          {["all", "photos", "videos"].map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setActiveFilter(filter)}
              className={`btn btn-outline-primary me-2 ${activeFilter === filter ? "active" : ""}`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}

          <button
            id="upload-combined-button"
            type="button"
            className="btn btn-primary"
            onClick={handleUpload}
            title="Maximum size: Image: 5MB, Video: 25MB"
          >
            + Upload
          </button>
        </div>
      </div>

      <div className="row g-2 user-files my-media-page">
        {loading
          ? placeholders
          : filteredMedia.map((item, index) => {
              if (item?.type === "image") {
                return (
                  <div key={`image-${item?.data?.idimage || index}`} className="col-6 col-md-3">
                    <ImageCard
                      image={item.data}
                      isMyMedia={true}
                      onRemove={handleImageRemoved}
                      onNoteSaved={handleImageNoteSaved}
                    />
                  </div>
                );
              }

              if (item?.type === "reel") {
                return (
                  <div key={`reel-${item?.data?.idreel || index}`} className="col-6 col-md-3">
                    <ReelCard
                      reel={item.data}
                      isMyMedia={true}
                      isPlaying={playingReelId === item.data.idreel}
                      onTogglePreview={handleTogglePreview}
                      onVideoEnded={handleVideoEnded}
                      onRemove={handleReelRemoved}
                    />
                  </div>
                );
              }

              return null;
            })}
      </div>

      <style>{`
        .my-media-page .new-tag {
          display: none !important;
        }
      `}</style>
    </AppLayout>
  );
}